import Sortable from "sortablejs";
import { uniqueId } from "underscore";
import BConfig from "../BConfig";
import BRequest from "../BRequest";
import BDragDropUploadFile from "./BDragDropUploadFile";

export default class BDragDropUploadForm
{
	constructor (el)
	{
		this.el = el;
	}

	static className = () => `drag-drop-upload`;
	static selector = () => `.${this.className()}`;

	static initialize()
	{
		this.addEventListeners();

		document.querySelectorAll(`.drag-drop-upload__files`).forEach((el) => {
			this.initializeContainer(el);
		});
	}

	static initializeContainer(container)
	{
		if (!container || container.dataset.sortableInit === "1") {
			return;
		}

		Sortable.create(container, {
			draggable: ".drag-drop-upload__file",
		});
		container.dataset.sortableInit = "1";
	}

	static addEventListeners()
	{
		const selector = this.selector();

		["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
			document.addEventListener(eventName, function(event) {
				if (event.target.closest(selector)) {
					event.preventDefault();
					event.stopPropagation();
				}
			});
		});

		["dragenter", "dragover"].forEach(eventName => {
			document.addEventListener(eventName, function(event) {
				if (event.target.closest(selector)) {
					const widget = new BDragDropUploadForm(event.target.closest(selector));
					widget.highlight(event);
				}
			});
		});

		["dragleave", "drop"].forEach(eventName => {
			document.addEventListener(eventName, function(event) {
				if (event.target.closest(selector)) {
					const widget = new BDragDropUploadForm(event.target.closest(selector));
					widget.unhighlight(event);
				}
			});
		});

		["drop"].forEach(eventName => {
			document.addEventListener(eventName, event => {
				if (event.target.closest(selector)) {
					const widget = new BDragDropUploadForm(event.target.closest(selector));
					widget.handleDrop(event);
				}
			});
		});

		["change"].forEach(eventName => {
			document.addEventListener(eventName, event => {
				if (event.target.matches(`${this.selector()} input[type="file"]`)) {
					const widget = new BDragDropUploadForm(event.target.closest(selector));

					let files = [];
					for (let i = 0; i < event.target.files.length; i++) {
						files.push(event.target.files[i]);
					}

					widget.handleUploads(files);
				}
			});
		});

		["change"].forEach(eventName => {
			document.addEventListener(eventName, event => {
				if (event.target.matches(`${this.selector()}__file-title-input`)) {
					const row = event.target.closest(`[data-storage-file-id]`);
					const storageFileId = row.dataset.storageFileId;

					let url = BConfig.getRoute("api.v2.storageFiles.edit")
						.replace("{storageFileId}", storageFileId)
						;

					let data = {
						title: row.querySelector(`${this.selector()}__file-title-input`).value,
					}

					return BRequest.patch(url, data).then(response => {
					}).catch(error => {
						console.log(error);
					});
				}
			});
		});

		["click"].forEach(eventName => {
			document.addEventListener(eventName, event => {
				const selector = `${this.selector()}__file-remove-button`;
				if (event.target.matches(`${selector}, ${selector} *`)) {
					event.preventDefault();

					const form = new BDragDropUploadForm(event.target.closest(BDragDropUploadForm.selector()));
					const file = new BDragDropUploadFile(event.target.closest(`[data-storage-file-id]`));
					file.remove();
					form.refreshState();
				}
			});
		});
	}

	highlight()
	{
		this.el.classList.add(`${BDragDropUploadForm.className()}--dragging`);
	}

	unhighlight()
	{
		this.el.classList.remove(`${BDragDropUploadForm.className()}--dragging`);
	}

	handleDrop(event)
	{
		let files = [];

		for (let i = 0; i < event.dataTransfer.items.length; i++) {
			if (event.dataTransfer.items[i].kind == "file") {
				files.push(event.dataTransfer.items[i].getAsFile());
			}
		}

		this.handleUploads(files);
	}

	handleUploads(uploads)
	{
		uploads.forEach(upload => {
			this.uploadFile(upload);
		});
	}

	getPrompt()
	{
		return this.el.querySelector(`${BDragDropUploadForm.selector()}__prompt`);
	}

	getContainer()
	{
		return this.el.querySelector(`${BDragDropUploadForm.selector()}__files`);
	}

	refreshState()
	{
		this.el.classList.toggle(`${BDragDropUploadForm.className()}--with-files`, this.getFiles().length);
	}

	getFiles()
	{
		return this.getContainer().querySelectorAll(`${BDragDropUploadForm.selector()}__file`);
	}

	getFileTemplate()
	{
		return this.el.querySelector(`${BDragDropUploadForm.selector()}__file-template`).content.cloneNode(true);
	}

	uploadFile(upload)
	{
		const url = BConfig.getRoute("api.v2.storageFiles.create");

		let data = new FormData;
		data.append("file", upload);
		if (this.el.dataset.storageDomain) {
			data.append("domain", this.el.dataset.storageDomain);
		}

		const uploadKey = uniqueId();

		let df = this.getFileTemplate();
		df.querySelector(BDragDropUploadFile.selector()).dataset.uploadKey = uploadKey;

		const container = this.getContainer();
		const addMore = container.querySelector(".drag-drop-upload__add-more");
		if (addMore) {
			container.insertBefore(df, addMore);
		} else {
			container.appendChild(df);
		}
		this.refreshState();

		let el = this.getContainer().querySelector(`[data-upload-key="${uploadKey}"]`);
		let file = new BDragDropUploadFile(el);

		return BRequest.post(url, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
			onUploadProgress: event => {
				file.setProgress(event);
			},
		}).then(response => {
			file.setFromResponse(response);
		}).catch(error => {
			console.error(error);
		}).finally(() => {
			this.refreshState();
		});
	}

	setFileInputName(fileInputName)
	{
		this.el.dataset.fileInputName = fileInputName;

		// Set value in template.
		this.el.querySelector(`.drag-drop-upload__file-template`).content.querySelector(`.drag-drop-upload__file-ids-input`).name = fileInputName;

		// Set value in id inputs.
		this.el.querySelectorAll(`.drag-drop-upload__file-ids-input`).forEach(el => {
			el.name = fileInputName;
		});
	}
}
